import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const choosingTheRightNameForYourBusinessContents = [
  'Understand Your Brand Identity',
  'Research Your Industry and Competitors',
  'Keep It Simple and Memorable',
  'Check for Availability',
  'Consider Future Growth',
  'Get Feedback',
  'Legal Considerations',
  'Ensure the Name is Culturally Appropriate',
  'Test the Name',
  'Utilize Keywords for SEO',
  'Use Online Tools for Inspiration',
  'Social Media and Marketing Considerations',
  'Reflect on Emotional Impact',
  'Avoid Overused Names',
  'Finalize and Protect Your Name',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const ChoosingTheRightNameForYourBusinessArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Selecting the right name for your business is a pivotal step in
        establishing your brand&apos;s identity. Your business name is often the
        first impression potential customers will have, and it plays a
        significant role in your marketing and branding efforts. A well-chosen
        name can help you stand out in a crowded market, attract customers, and
        build a strong brand. This comprehensive guide will walk you through the
        key considerations and steps involved in choosing a name that aligns
        with your business goals and resonates with your target audience.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Understand Your Brand Identity
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Defining Your Brand
        </Layout.Card.Text>
        <Layout.Card.Text>
          Before you start brainstorming names, it&apos;s crucial to have a
          clear understanding of your brand&apos;s identity. Consider what your
          brand stands for, its core values, mission, and the message you want
          to convey. Are you aiming for a professional, trustworthy image, or
          something more fun and creative? Your business name should reflect
          this vision.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Research Your Industry and Competitors
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Analyzing Market Trends
        </Layout.Card.Text>
        <Layout.Card.Text>
          Look at the naming trends in your industry. What types of names are
          your competitors using? This research can provide insight into what
          works and what doesn&apos;t. Avoid choosing a name that is too similar
          to competitors, as it can lead to confusion and potential legal
          issues.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Keep It Simple and Memorable
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Importance of Simplicity
        </Layout.Card.Text>
        <Layout.Card.Text>
          A simple, easy-to-pronounce, and memorable name is more likely to
          stick with customers. Overly complex or lengthy names can be difficult
          to remember or spell, reducing their effectiveness. Think of
          successful brands like Apple, Nike, and Google – their names are
          short, simple, and easy to remember.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Check for Availability
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Domain Name and Trademarks
        </Layout.Card.Text>
        <Layout.Card.Text>
          Ensure the name you want is available as a domain name for your
          website. Also, check for trademark registrations to avoid legal
          conflicts. You can use online tools to check domain availability and
          the USPTO database for trademarks.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Consider Future Growth
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Scalability of the Name
        </Layout.Card.Text>
        <Layout.Card.Text>
          Think about your long-term business goals. Choose a name that can grow
          with your business and won&apos;t limit your expansion into new
          products or markets. A name that is too specific might hinder future
          diversification.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${5}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Get Feedback
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Seeking Opinions
        </Layout.Card.Text>
        <Layout.Card.Text>
          Gather feedback from friends, family, and potential customers. They
          can provide valuable insights and help you gauge the name&apos;s
          effectiveness. Sometimes, an outside perspective can highlight issues
          or strengths you hadn&apos;t considered.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${6}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Legal Considerations
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Avoiding Unintended Meanings
        </Layout.Card.Text>
        <Layout.Card.Text>
          Make sure your business name doesn&apos;t have negative or unintended
          meanings in other languages or cultures, especially if you plan to
          operate internationally. This can help you avoid potential
          embarrassment and ensure your brand is well-received globally.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${7}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Ensure the Name is Culturally Appropriate
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Registering Your Business Name
        </Layout.Card.Text>
        <Layout.Card.Text>
          Once you&apos;ve chosen a name, you&apos;ll need to register it with
          the appropriate government authorities. This typically involves
          registering your business entity with your state and filing for any
          necessary licenses or permits. Additionally, consider securing your
          name with a trademark to protect it from being used by others.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${8}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Test the Name
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Evaluating Market Reception
        </Layout.Card.Text>
        <Layout.Card.Text>
          Before making a final decision, test the name in the market. Use focus
          groups or small-scale advertising campaigns to see how your target
          audience reacts to the name. This can provide valuable insights and
          confirm whether the name resonates with your potential customers.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${9}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Utilize Keywords for SEO
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Incorporating Relevant Keywords
        </Layout.Card.Text>
        <Layout.Card.Text>
          If possible, incorporate relevant keywords into your business name to
          enhance your search engine optimization (SEO) efforts. For example, if
          you&apos;re starting a bakery, including the word “bakery” in your
          name can help improve your search engine rankings when potential
          customers search for bakeries online.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${10}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Use Online Tools for Inspiration
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Leveraging Name Generators
        </Layout.Card.Text>
        <Layout.Card.Text>
          There are numerous online tools and name generators that can help you
          brainstorm ideas. These tools can provide creative suggestions and
          help you find a unique name that fits your brand.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${11}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Social Media and Marketing Considerations
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Checking Social Media Availability
        </Layout.Card.Text>
        <Layout.Card.Text>
          In today&apos;s digital age, it&apos;s essential to have a consistent
          brand presence across various platforms. Ensure your chosen name is
          available as a handle on social media platforms like Twitter,
          Instagram, and Facebook. Consistency in your business name across all
          platforms makes it easier for customers to find and engage with you.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${12}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Reflect on Emotional Impact
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Connecting with Your Audience
        </Layout.Card.Text>
        <Layout.Card.Text>
          Consider the emotional impact of your business name. A name that
          evokes positive emotions can create a strong connection with your
          audience. Think about the feelings you want to elicit in your
          customers and choose a name that aligns with those emotions.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${13}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Avoid Overused Names
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Standing Out in the Market
        </Layout.Card.Text>
        <Layout.Card.Text>
          Avoid names that are too generic or overused in your industry. A
          unique name helps you stand out in the market and makes it easier for
          customers to remember and find you. Conduct a thorough search to
          ensure your name isn’t too similar to existing businesses.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${14}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Finalize and Protect Your Name
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Securing Your Brand Identity
        </Layout.Card.Text>
        <Layout.Card.Text>
          Once you&apos;ve settled on a name, take steps to protect it. Register
          your business name with the relevant authorities, secure domain names,
          and consider applying for a trademark. These actions help safeguard
          your brand and prevent others from using your name.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Choosing the right name for your business is a critical step that
        requires careful consideration and research. By understanding your brand
        identity, keeping the name simple and memorable, ensuring availability,
        and planning for future growth, you can select a name that sets your
        business up for success.
      </Layout.Card.Text>
      <Layout.Card.Text>
        For more guidance on choosing and registering your business name,
        visit&nbsp;<Link href="/">Incorpified</Link>. Our experts can help you
        navigate the process and ensure your business name aligns with your
        brand and goals.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

ChoosingTheRightNameForYourBusinessArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default ChoosingTheRightNameForYourBusinessArticle;
